import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { emergencyApi } from '../services/api';
import { format } from 'date-fns';

interface EmergencyContact {
  contactId: string;
  contactName: string;
  relationship: string;
  addedOn: string;
}

export default function EmergencyLookup() {
  const [email, setEmail] = useState('');
  const [contacts, setContacts] = useState<EmergencyContact[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await emergencyApi.lookupByEmail(email.trim());
      const data = response.data || [];
      setContacts(data);

      if (data.length === 0) {
        setError('No emergency contacts found for this email address. Please check with the parent/guardian.');
      }
    } catch (err) {
      setError((err as Error).message || 'Failed to lookup emergency contacts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestAccess = (contactId: string) => {
    // Navigate to the request access page with the contact ID
    navigate(`/emergency/request/${contactId}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-12">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
            <span className="text-4xl">🚨</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-3">
            Emergency Access
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-lg mx-auto">
            If you are an emergency contact for a child, enter your email address below to request access to their medical information.
          </p>
        </div>

        {/* Form or Results */}
        {!contacts ? (
          <div className="card p-8">
            <form onSubmit={handleLookup} className="space-y-6">
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input text-lg py-3"
                  placeholder="you@example.com"
                  autoFocus
                />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  This must match the email address provided by the parent/guardian.
                </p>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-danger w-full text-lg py-3"
              >
                {isLoading ? (
                  <>
                    <span className="spinner mr-2" />
                    Looking up...
                  </>
                ) : (
                  'Continue'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/"
                className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
              >
                Back to home
              </Link>
            </div>
          </div>
        ) : (
          <div className="card p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Emergency Contacts Found
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You are listed as an emergency contact for the following:
            </p>

            <div className="space-y-4">
              {contacts.map((contact) => (
                <div
                  key={contact.contactId}
                  className="p-5 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {contact.contactName}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Relationship: {contact.relationship}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Added: {format(new Date(contact.addedOn), 'PPP')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRequestAccess(contact.contactId)}
                    className="btn-danger w-full"
                  >
                    Request Emergency Access
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setContacts(null);
                  setEmail('');
                  setError(null);
                }}
                className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
              >
                Search again
              </button>
            </div>
          </div>
        )}

        {/* Safety Notice */}
        <div className="mt-8 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <p className="text-sm text-yellow-800 dark:text-yellow-200 text-center">
            <strong>Important:</strong> Emergency access requests are logged and audited. Only use this feature in genuine emergencies.
          </p>
        </div>
      </div>
    </div>
  );
}
